import type { TFunction } from "i18next";

/**
 * Segment / urban phrase tokens that the CADA peer-group loader composes labels
 * from (e.g. "Non-Luxury Low-Vol.", "MB Rural", "Luxury Hi-Vol.", "Hi-Vol.").
 * Each maps to a French translation under the `peerGroupPhrases` i18n namespace.
 * Order-independent — we peel recognized tokens off the END of the label.
 */
const PHRASE_TOKENS = [
    "Non-Luxury",
    "Luxury",
    "Low-Vol.",
    "Med-Vol.",
    "Hi-Vol.",
    "Rural",
    "Urban",
];

/**
 * Localize a peer-group dropdown label.
 *
 * Peer-group labels are composed by the backend as an optional prefix (province
 * code / franchise / dealer group — kept verbatim) followed by one or more
 * segment/urban phrase tokens, e.g. "MB Non-Luxury Low-Vol." = "MB" + "Non-Luxury"
 * + "Low-Vol.". Only the French (CADA) site recomposes them: each recognized
 * phrase is translated and multiple phrases are joined with a comma (per Annaliese,
 * ES-1026). Every other locale keeps the stored English label, with the existing
 * exact-key translations (e.g. "All Car" -> "Toutes les voitures") as a fallback.
 */
export function translatePeerGroupLabel(
    label: string,
    t: TFunction,
    lang: string | undefined,
): string {
    if (!label) return label;

    // Exact curated key first (e.g. "All Car" -> "Toutes les voitures"). Mirrors
    // translateJobTitle so any curated composite entry wins over recomposition.
    const exact = t(`peerGroups.${label}`, "");
    if (exact) return exact;

    // Only the French (CADA) site recomposes; other locales keep the raw English.
    if (!lang?.startsWith("fr")) return label;

    // Peel recognized phrase tokens off the end; the remainder is the prefix
    // (province / franchise / group name), kept verbatim.
    const words = label.split(" ");
    let i = words.length;
    while (i > 0 && PHRASE_TOKENS.includes(words[i - 1])) {
        i -= 1;
    }
    const phrases = words.slice(i);
    if (phrases.length === 0) return label; // franchise/region/group labels

    const prefix = words.slice(0, i).join(" ");
    const translated = phrases.map((p) => t(`peerGroupPhrases.${p}`, p)).join(", ");
    return prefix ? `${prefix} ${translated}` : translated;
}
